import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, query, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import Header from '@/components/layout/Header';
import {
  ArrowLeft,
  Search,
  Calendar,
  Building2,
  Users,
  Eye,
  Filter,
  ArrowUpDown,
  MapPin,
  ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';

interface EventWithUnit {
  id: string;
  unitId: string;
  unitName: string;
  unitNumber: string;
  unitTypeName: string;
  unitEntityName: string;
  name: string;
  date: Date | null;
  location: string;
  currentAttendees: number;
  maxCapacity?: number;
  eventStatus: string;
  isPublic?: boolean;
  createdAt: Date;
}

type SortField = 'date' | 'name' | 'unitName' | 'currentAttendees' | 'eventStatus';
type SortDirection = 'asc' | 'desc';

const EVENT_STATUS_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  draft: { label: 'Draft', variant: 'secondary' },
  'interest-only': { label: 'Interest Only', variant: 'outline' },
  published: { label: 'Published', variant: 'default' },
  closed: { label: 'Closed', variant: 'outline' },
  completed: { label: 'Completed', variant: 'secondary' },
  deleted: { label: 'Deleted', variant: 'destructive' },
};

export default function EventManagementPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<EventWithUnit[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<EventWithUnit[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    fetchAllEvents();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [events, searchQuery, statusFilter, sortField, sortDirection]);

  const fetchAllEvents = async () => {
    setLoading(true);
    try {
      // Step 1: Fetch all units with their type info
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
      const unitTypesMap = new Map<string, { name: string; entityName: string }>();
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        if (settings.unitTypes) {
          settings.unitTypes.forEach((t: any) => {
            unitTypesMap.set(t.id, { name: t.name, entityName: t.entityName });
          });
        }
      }

      const unitsSnapshot = await getDocs(collection(db, 'units'));
      const allEvents: EventWithUnit[] = [];

      // Step 2: For each unit, fetch its events
      for (const unitDoc of unitsSnapshot.docs) {
        const unitData = unitDoc.data();
        const unitId = unitDoc.id;
        const unitName = unitData.name || '';
        const unitNumber = unitData.unitNumber || unitData.number || '';
        const typeId = unitData.typeId || '';
        const typeInfo = unitTypesMap.get(typeId);
        const unitTypeName = typeInfo?.name || '';
        const unitEntityName = typeInfo?.entityName || 'Unit';

        try {
          const eventsQuery = query(
            collection(db, 'units', unitId, 'events'),
            orderBy('date', 'desc')
          );
          const eventsSnapshot = await getDocs(eventsQuery);

          eventsSnapshot.forEach((eventDoc) => {
            const data = eventDoc.data();

            // Parse date
            let eventDate: Date | null = null;
            if (data.date) {
              if (data.date.toDate && typeof data.date.toDate === 'function') {
                eventDate = data.date.toDate();
              } else if (data.date instanceof Date) {
                eventDate = data.date;
              } else if (typeof data.date === 'string') {
                const parsed = new Date(data.date);
                eventDate = isNaN(parsed.getTime()) ? null : parsed;
              }
            }

            // Parse createdAt
            let createdAt: Date = new Date();
            if (data.createdAt) {
              if (data.createdAt.toDate && typeof data.createdAt.toDate === 'function') {
                createdAt = data.createdAt.toDate();
              } else if (data.createdAt instanceof Date) {
                createdAt = data.createdAt;
              } else if (typeof data.createdAt === 'string') {
                const parsed = new Date(data.createdAt);
                if (!isNaN(parsed.getTime())) createdAt = parsed;
              }
            }

            allEvents.push({
              id: eventDoc.id,
              unitId,
              unitName,
              unitNumber,
              unitTypeName,
              unitEntityName,
              name: data.name || data.title || 'Untitled Event',
              date: eventDate,
              location: data.location || '',
              currentAttendees: data.currentAttendees || 0,
              maxCapacity: data.maxCapacity,
              eventStatus: data.eventStatus || 'draft',
              isPublic: data.isPublic || false,
              createdAt,
            });
          });
        } catch (err) {
          console.warn(`⚠️ Could not fetch events for unit ${unitId}:`, err);
        }
      }

      console.log(`✅ Fetched ${allEvents.length} events across ${unitsSnapshot.size} units`);
      setEvents(allEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...events];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((e) => e.eventStatus === statusFilter);
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.unitName.toLowerCase().includes(q) ||
          e.unitNumber.toLowerCase().includes(q) ||
          e.unitTypeName.toLowerCase().includes(q) ||
          e.location.toLowerCase().includes(q)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case 'date':
          aVal = a.date?.getTime() ?? 0;
          bVal = b.date?.getTime() ?? 0;
          break;
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'unitName':
          aVal = a.unitName.toLowerCase();
          bVal = b.unitName.toLowerCase();
          break;
        case 'currentAttendees':
          aVal = a.currentAttendees;
          bVal = b.currentAttendees;
          break;
        case 'eventStatus':
          aVal = a.eventStatus;
          bVal = b.eventStatus;
          break;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredEvents(filtered);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'date' ? 'desc' : 'asc');
    }
  };

  const handleViewEvent = (event: EventWithUnit) => {
    const diningUrl = import.meta.env.VITE_DINING_URL || 'http://localhost:3000';
    window.open(`${diningUrl}/unit/${event.unitId}/event/${event.id}`, '_blank');
  };

  const getStatusBadge = (status: string) => {
    const config = EVENT_STATUS_CONFIG[status] || { label: status, variant: 'outline' as const };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3 text-muted-foreground opacity-50" />;
    }
    return (
      <ArrowUpDown
        className={`ml-1 h-3 w-3 ${sortDirection === 'asc' ? 'text-primary' : 'text-primary rotate-180'} transition-transform`}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading events...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 flex flex-col">
        <div className="px-4 py-8">
          <div className="container mx-auto">
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Event Management</h1>
                <p className="text-muted-foreground">View and manage all events across the platform</p>
              </div>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </div>

            {/* Search and Filter */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-base">Search &amp; Filter</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-4 md:flex-row md:items-end">
                  <div className="flex-1">
                    <Label className="text-sm mb-1 block">Search</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search by event name, unit name, number, or location..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex items-end gap-2">
                    <div>
                      <Label className="text-sm mb-1 block flex items-center gap-1">
                        <Filter className="h-3 w-3" /> Status
                      </Label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring h-10"
                      >
                        <option value="all">All Statuses</option>
                        <option value="draft">Draft</option>
                        <option value="interest-only">Interest Only</option>
                        <option value="published">Published</option>
                        <option value="closed">Closed</option>
                        <option value="completed">Completed</option>
                        <option value="deleted">Deleted</option>
                      </select>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Full-width events table */}
        <div className="flex-1 px-4 pb-8">
          <div className="container mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Events ({filteredEvents.length})
                </CardTitle>
                <CardDescription>
                  {filteredEvents.length === events.length
                    ? `Showing all ${events.length} events`
                    : `Showing ${filteredEvents.length} of ${events.length} events`}
                  {' '}· Sorted by date (newest first by default)
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {filteredEvents.length === 0 ? (
                  <div className="py-16 text-center">
                    <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4 opacity-50" />
                    <p className="text-muted-foreground font-medium">No events found</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {searchQuery || statusFilter !== 'all'
                        ? 'Try adjusting your search or filters'
                        : 'No events have been created yet'}
                    </p>
                  </div>
                ) : (
                  <div className="rounded-b-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50 transition-colors w-[200px]"
                            onClick={() => handleSort('date')}
                          >
                            <span className="flex items-center">
                              Date <SortIcon field="date" />
                            </span>
                          </TableHead>
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50 transition-colors"
                            onClick={() => handleSort('name')}
                          >
                            <span className="flex items-center">
                              Event Name <SortIcon field="name" />
                            </span>
                          </TableHead>
                          <TableHead className="w-[120px]">Unit Type</TableHead>
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50 transition-colors w-[220px]"
                            onClick={() => handleSort('unitName')}
                          >
                            <span className="flex items-center">
                              Unit <SortIcon field="unitName" />
                            </span>
                          </TableHead>
                          <TableHead className="w-[160px]">Location</TableHead>
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50 transition-colors w-[110px] text-center"
                            onClick={() => handleSort('currentAttendees')}
                          >
                            <span className="flex items-center justify-center">
                              Attending <SortIcon field="currentAttendees" />
                            </span>
                          </TableHead>
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50 transition-colors w-[130px]"
                            onClick={() => handleSort('eventStatus')}
                          >
                            <span className="flex items-center">
                              Status <SortIcon field="eventStatus" />
                            </span>
                          </TableHead>
                          <TableHead className="w-[100px]" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredEvents.map((event) => (
                          <TableRow key={`${event.unitId}-${event.id}`} className="hover:bg-muted/50">
                            <TableCell className="text-sm font-medium">
                              {event.date && !isNaN(event.date.getTime()) ? (
                                <div>
                                  <div>{format(event.date, 'dd MMM yyyy')}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {format(event.date, 'HH:mm')}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-xs">Date not set</span>
                              )}
                            </TableCell>
                            <TableCell className="font-medium">
                              <div className="flex flex-col">
                                <span className="truncate max-w-[240px]" title={event.name}>
                                  {event.name}
                                </span>
                                {event.isPublic && (
                                  <Badge variant="outline" className="text-xs w-fit mt-0.5">
                                    Public
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {event.unitTypeName ? (
                                <Badge variant="outline" className="text-xs">
                                  {event.unitTypeName}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">
                              <div className="flex items-start gap-1">
                                <Building2 className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                                <div>
                                  <div
                                    className="font-medium truncate max-w-[180px]"
                                    title={event.unitName}
                                  >
                                    {event.unitName}
                                  </div>
                                  {event.unitNumber && (
                                    <div className="text-xs text-muted-foreground">
                                      No. {event.unitNumber}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {event.location ? (
                                <div className="flex items-start gap-1">
                                  <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                                  <span
                                    className="truncate max-w-[140px] text-muted-foreground"
                                    title={event.location}
                                  >
                                    {event.location}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="font-medium">{event.currentAttendees}</span>
                                {event.maxCapacity && (
                                  <span className="text-muted-foreground text-xs">
                                    /{event.maxCapacity}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{getStatusBadge(event.eventStatus)}</TableCell>
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewEvent(event)}
                                className="h-8 gap-1.5"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                View
                                <ExternalLink className="h-3 w-3 text-muted-foreground" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
